import * as Sentry from "@sentry/nuxt";
import { AuthError } from "aws-amplify/auth";

import CallToActionToast from "~/components/base/feedback/BaseCallToActionToast.vue";

function getRandomProbability() {
  return crypto.getRandomValues(new Uint32Array(1))[0] || 0;
}

function generateRandomPassword(length: number): string {
  const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
  const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numberChars = "0123456789";
  const specialChars = "!@#$%^&*-_=+[]{}|;:,.<>?";

  const allChars = lowercaseChars + uppercaseChars + numberChars + specialChars;
  const passwordArray: string[] = [];

  // Ensure the password contains at least one character from each group
  passwordArray.push(
    lowercaseChars.charAt(getRandomProbability() % lowercaseChars.length),
  );
  passwordArray.push(
    uppercaseChars.charAt(getRandomProbability() % uppercaseChars.length),
  );
  passwordArray.push(
    numberChars.charAt(getRandomProbability() % numberChars.length),
  );
  passwordArray.push(
    specialChars.charAt(getRandomProbability() % specialChars.length),
  );

  // Fill the remaining password length with random characters from all groups
  for (let i = passwordArray.length; i < length; i++) {
    const randomIndex = getRandomProbability() % allChars.length;
    passwordArray.push(allChars.charAt(randomIndex));
  }

  // Shuffle the array using Fisher-Yates with crypto for uniform distribution
  for (let i = passwordArray.length - 1; i > 0; i--) {
    const j = Math.floor((getRandomProbability() / 0xffffffff) * (i + 1));
    const temp = passwordArray[i];
    passwordArray[i] = passwordArray[j] as string;
    passwordArray[j] = temp as string;
  }

  return passwordArray.join("");
}

export default defineNuxtPlugin({
  name: "authenticateWithLiverpool",
  parallel: true,
  dependsOn: ["setInitialAuthState"],
  setup: async () => {
    if (!getIsHostLiverpool()) return;

    const auth = useAuth();
    if (auth?.isLoggedIn.value) return;

    const lfcLoginCookie = useCookie<LfcLoginCookie>("lfcLogin");
    const username = lfcLoginCookie.value?.id; // we are using the lfc_id as the username so that if they change their email address on the lfc-side, we don't have login issues
    const email = lfcLoginCookie.value?.username;
    if (!username || !email || !lfcLoginCookie.value?.dob) return;

    const isUserAuthenticatedOnLiverpool =
      await getIsUserAuthenticatedOnLiverpool();

    if (!isUserAuthenticatedOnLiverpool) {
      // the user is not authenticated on Liverpool FC, let them just continue as an anonymous user
      return;
    }

    const toast = useToast();

    const { differenceInYears, parse } = await import("date-fns");

    const dateOfBirth = parse(
      lfcLoginCookie.value.dob,
      "yyyy-MM-ddxxxxx",
      new Date(),
    );

    if (differenceInYears(new Date(), dateOfBirth) < 18) {
      toast.error(
        "We're sorry, but you need to be at least 18 years old to log in to the Liverpool FC Official Auction Store.",
      );

      return;
    }

    try {
      await auth.loginWith({
        strategy: "passwordless",
        options: {
          username,
        },
      });
    } catch (e) {
      try {
        if (!(e instanceof AuthError) || e.name !== "UserNotFoundException") {
          throw e;
        }

        await auth.register({
          email: username,
          password: generateRandomPassword(32),
          userAttributes: {
            email,
            "custom:lfc_id": lfcLoginCookie.value.id,
          },
        });
      } catch (e) {
        Sentry.captureException(e);
        toast.error({
          component: CallToActionToast,
          props: {
            htmlMessage:
              'We encountered an issue while trying to log you in. Please try again and if the problem persists, <a href="https://support.liverpool.mws.com" class="underline">contact support</a> for further help.',
          },
        });
      }
    }
  },
});
